import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { nhisDashboardGet, MOCK_DASHBOARD } from '../utils/nhisDashboardApi';
import NhisHeroBanner from '../components/nhis-dashboard/NhisHeroBanner';
import NhisHealthSnapshot from '../components/nhis-dashboard/NhisHealthSnapshot';
import NhisTrendChart from '../components/nhis-dashboard/NhisTrendChart';
import NhisCancerScreening from '../components/nhis-dashboard/NhisCancerScreening';
import NhisMedicalUsage from '../components/nhis-dashboard/NhisMedicalUsage';
import NhisMedicationSummary from '../components/nhis-dashboard/NhisMedicationSummary';

// 기본은 실제 API 호출, 필요 시에만 명시적으로 mock 사용
const USE_MOCK = process.env.REACT_APP_NHIS_DASHBOARD_MOCK === 'true';

export default function NhisDashboardPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAnalysisToast, setShowAnalysisToast] = useState(false);
  const [refreshSeq, setRefreshSeq] = useState(0);
  const basePath = location.pathname.startsWith('/app') ? '/app' : '';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    const load = async () => {
      try {
        const result = USE_MOCK ? MOCK_DASHBOARD : await nhisDashboardGet();
        if (!cancelled) setData(result);
      } catch (e) {
        if (!cancelled) setError(e?.message || '데이터를 불러오지 못했습니다.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [refreshSeq]);

  useEffect(() => {
    if (!location.state?.analysisStartedToast) return;
    setShowAnalysisToast(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!showAnalysisToast) return undefined;
    const timer = setTimeout(() => setShowAnalysisToast(false), 4000);
    return () => clearTimeout(timer);
  }, [showAnalysisToast]);

  const hasDashboardData = Boolean(data && (
    data.latestCheckup ||
    (Array.isArray(data.checkupHistory) && data.checkupHistory.length > 0) ||
    (Array.isArray(data.trendData) && data.trendData.length > 0) ||
    (Array.isArray(data.cancerScreenings) && data.cancerScreenings.length > 0) ||
    data.medicalUsage ||
    data.medication ||
    (Array.isArray(data.healthCheckupOverview?.commonItems) && data.healthCheckupOverview.commonItems.length > 0)
  ));
  const missingSections = data && hasDashboardData ? [
    !(data.latestCheckup || (Array.isArray(data.checkupHistory) && data.checkupHistory.length > 0)) && '건강검진 요약',
    !(Array.isArray(data.trendData) && data.trendData.length > 0) && '변화 추이',
    !(
      (Array.isArray(data.healthCheckupOverview?.commonItems) && data.healthCheckupOverview.commonItems.length > 0) ||
      (Array.isArray(data.healthCheckupOverview?.optionalItems) && data.healthCheckupOverview.optionalItems.length > 0)
    ) && '검진 항목',
    !(Array.isArray(data.cancerScreenings) && data.cancerScreenings.length > 0) && '암 검진 이력',
    !data.medicalUsage && '진료 이용',
    !data.medication && '투약 정보',
  ].filter(Boolean) : [];

  return (
    <div style={{ minHeight: '100vh', margin: '0 -20px', overflowX: 'hidden', boxSizing: 'border-box' }}>
      {showAnalysisToast && (
        <div role="status" aria-live="polite" style={{
          position: 'fixed', top: 24, left: '50%', transform: 'translateX(-50%)',
          zIndex: 2000, width: 'calc(100% - 48px)', maxWidth: 420,
          background: '#1f2937', borderRadius: 14,
          padding: '14px 18px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          animation: 'fadeInDown 0.3s ease',
        }}>
          <style>{`@keyframes fadeInDown { from { opacity:0; transform:translateX(-50%) translateY(-10px); } to { opacity:1; transform:translateX(-50%) translateY(0); } }`}</style>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{
              width: 20, height: 20, borderRadius: 999, background: '#22c55e', flexShrink: 0,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }} aria-hidden="true" />
            <div>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#fff' }}>분석이 시작되었습니다</p>
              <p style={{ margin: '3px 0 0', fontSize: 12, color: '#9ca3af', lineHeight: 1.4 }}>
                약 3~5분 후 완료 알림을 보내드립니다
              </p>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div role="status" aria-live="polite" aria-busy="true" style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', paddingTop: 100, gap: 12,
        }}>
          <div aria-hidden="true" style={{
            width: 36, height: 36, borderRadius: '50%',
            border: '3px solid #e5e7eb', borderTopColor: '#1763ff',
            animation: 'nhis-spin 0.8s linear infinite',
          }} />
          <p style={{ margin: 0, fontSize: 14, color: '#6b7280' }}>건강 데이터를 불러오는 중...</p>
          <style>{`@keyframes nhis-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {!loading && error && (
        <div role="alert" aria-labelledby="nhis-dashboard-error-title" aria-describedby="nhis-dashboard-error-description" style={{
          margin: '40px 16px', padding: '20px 24px',
          background: '#fff1f2', borderRadius: 14,
          border: '1px solid #fecdd3', textAlign: 'center',
        }}>
          <p id="nhis-dashboard-error-title" style={{ margin: 0, fontSize: 14, color: '#cb3553', fontWeight: 600 }}>데이터 조회 실패</p>
          <p id="nhis-dashboard-error-description" style={{ margin: '6px 0 0', fontSize: 13, color: '#374151' }}>{error}</p>
          <button
            type="button"
            onClick={() => setRefreshSeq((value) => value + 1)}
            style={{
              marginTop: 14, padding: '8px 20px', borderRadius: 8,
              background: '#cb3553', color: '#fff', border: 'none',
              fontSize: 13, fontWeight: 700, cursor: 'pointer',
            }}
          >
            다시 시도
          </button>
        </div>
      )}

      {!loading && !error && data && !hasDashboardData && (
        <div role="status" aria-live="polite" style={{
          margin: '40px 16px', padding: '24px',
          background: '#f8fafc', borderRadius: 16,
          border: '1px solid #dbe6f3', textAlign: 'center',
        }}>
          <p style={{ margin: 0, fontSize: 15, color: '#102036', fontWeight: 800 }}>아직 보여줄 건강 데이터가 없습니다</p>
          <p style={{ margin: '8px 0 0', fontSize: 13, color: '#61758e', lineHeight: 1.6 }}>
            마이데이터 수집을 완료하면 검진, 진료, 투약 정보를 이곳에서 확인할 수 있습니다.
          </p>
          <button
            type="button"
            onClick={() => navigate(basePath + '/mydata/connect/consent')}
            style={{
              marginTop: 16, padding: '10px 18px', borderRadius: 999,
              background: '#1763ff', color: '#fff', border: 'none',
              fontSize: 13, fontWeight: 800, cursor: 'pointer',
            }}
          >
            마이데이터 연결하기
          </button>
        </div>
      )}

      {!loading && !error && data && hasDashboardData && (
        <>
          <NhisHeroBanner latestCheckup={data.latestCheckup} checkupHistory={data.checkupHistory} />

          <div style={{ padding: '12px 16px 40px', display: 'grid', gap: 12, boxSizing: 'border-box' }}>
            {missingSections.length > 0 && (
              <div role="status" aria-live="polite" style={{
                padding: '12px 14px',
                borderRadius: 14,
                background: '#f8fbff',
                border: '1px solid #dbe6f3',
                color: '#37547c',
                fontSize: 12,
                lineHeight: 1.55,
              }}>
                <b style={{ display: 'block', marginBottom: 3, color: '#18395f' }}>일부 데이터는 아직 준비 중입니다</b>
                {missingSections.join(', ')} 데이터가 수집되면 이 화면에 자동으로 추가됩니다.
              </div>
            )}
            <NhisTrendChart
              trendData={data.trendData}
              healthCheckupOverview={data.healthCheckupOverview}
              patientGender={data.patientGender}
            />
            <NhisHealthSnapshot
              healthCheckupOverview={data.healthCheckupOverview}
            />
            <NhisCancerScreening cancerScreenings={data.cancerScreenings} />
            <NhisMedicalUsage medicalUsage={data.medicalUsage} />
            <NhisMedicationSummary medication={data.medication} />
          </div>
        </>
      )}
    </div>
  );
}
